import { Injectable, NgZone } from '@angular/core';
import { GoogleNormalisedAddress } from './google-normalised-address';
import { MapsLoaderService } from './maps-loader.service';
import { WindowRef } from '@vendor/window/window.component';
import { Company } from '@core/models/common/company';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { $log } from '@core/utils/logger';
import { MapTravelMode } from '@core/models/map/map-travel-mode';

@Injectable()
export class PlacesService {

    private placesServiceRef: any;
    private nativeDocument: any;
    private addressFields: any;

    constructor(private windowRef: WindowRef,
                private httpClient: HttpClient,
                private ngZone: NgZone,
                private mapsLoader: MapsLoaderService) {
        this.nativeDocument = this.windowRef.nativeWindow.document;

        this.addressFields = {
            subpremise: 'short_name',
            street_number: 'short_name',
            route: 'long_name',
            locality: 'long_name',
            administrative_area_level_1: 'short_name',
            administrative_area_level_2: 'short_name',
            country: 'long_name',
            postal_code: 'short_name'
        };
    }

    getDetails(ref: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const w: any = this.windowRef.nativeWindow;
            this.ngZone.run(() => {
                this.mapsLoader.onLoad().then(() => {
                    if (!this.placesServiceRef) {
                        this.placesServiceRef = new w.google.maps.places.PlacesService(this.nativeDocument.createElement('div'));
                    }

                    this.placesServiceRef.getDetails({
                        place_id: ref,
                        placeId: ref
                    }, (data, status) => {

                        if (status === w.google.maps.places.PlacesServiceStatus.OK) {
                            resolve(data);
                        } else {
                            reject();
                        }
                    });
                }, (error) => {
                    $log.error('Maps API failed:', error);
                    reject();
                });
            });
        });
    }

    resolveLocationFromPlace(place) {

        // Normalise the structured address data to build the default location name
        const normalizedAddress: any = {};
        if (place.address_components) {
            let addressType;
            let addressTypeValue;
            for (let i = 0, len = place.address_components.length; i < len; i++) {
                addressType = place.address_components[i].types[0];
                addressTypeValue = this.addressFields[addressType];
                if (addressTypeValue) {
                    normalizedAddress[addressType] = place.address_components[i][addressTypeValue];
                }
            }
        }

        return normalizedAddress.locality || '';
    }

    normaliseAddressFromPlace(place: any): GoogleNormalisedAddress {
        let normalisedAddress: GoogleNormalisedAddress = new GoogleNormalisedAddress();

        if (place.address_components) {
            let addressType;
            let addressTypeValue;
            for (let i = 0, len = place.address_components.length; i < len; i++) {
                addressType = place.address_components[i].types[0];
                addressTypeValue = this.addressFields[addressType];
                if (addressTypeValue) {
                    normalisedAddress[addressType] = place.address_components[i]['long_name'] || null;
                    normalisedAddress[addressType + '_short'] = place.address_components[i]['short_name'] || null;
                }
            }
        }

        return normalisedAddress;
    }

    getCoordinates(place: any): {lat: number, lng: number} {
        const result = {
            lat: null,
            lng: null
        };

        if(place.geometry && place.geometry.location) {
            result.lat = place.geometry.location.lat();
            result.lng = place.geometry.location.lng()
        }

        return result;
    }

    getStreetLine1(address: any): string {
        let result: string = '';
        result += (address.subpremise && address.subpremise.length ? address.subpremise + '/' : '');
        result += (address.street_number ? address.street_number + ' ' : '');
        result += (address.route_short || address.route || '');

        return result;
    }

    getGoogleDirections(origin: string, destination: string, travelMode?: MapTravelMode) {
        const url: string = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelMode=${travelMode}`;
        return url;
    }

    getGoogleSearch(company: Company) {
        const url: string = `https://www.google.com/maps/search/?api=1&query=${company.address}`;
        return url;
    }

    getTimezoneFromLatitudAndLongitude(lat: number, lng: number) {
        const url: string = `https://maps.googleapis.com/maps/api/timezone/json?location=${ lat },${ lng }&timestamp=${ moment().unix() }&key=${ environment.google.key }`;
        return this.httpClient.get(url);
    }
}
